import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { TimeRange, RangeKey } from '../types/superadmin';

export const DEFAULT_RANGE: TimeRange = { key: 'last30' };

const LAST_30_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Two months side by side, dropping to one when the popover cannot fit them. */
const TWO_MONTH_MIN_WIDTH = 640;

const PRESETS: { key: RangeKey; label: string }[] = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' }
];

/**
 * All range maths runs in UTC to match the backend, which buckets days in UTC.
 * Using local dates here would shift the range by a day for anyone west of
 * Greenwich — they would ask for the 5th and get the 4th.
 */
const toIsoDay = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;

/** Parse 'YYYY-MM-DD' as UTC midnight, or undefined when absent/invalid. */
function fromIsoDay(value?: string): Date | undefined {
  if (!value) return undefined;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

const utcToday = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

/** Start of an N-day window ending today, inclusive of both ends. */
function startOfRollingWindow(days: number): Date {
  return new Date(utcToday().getTime() - (days - 1) * MS_PER_DAY);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '5 Jul 2026' — unambiguous across locales, unlike a numeric date. */
function formatDay(value?: string): string {
  const date = fromIsoDay(value);
  if (!date) return '—';
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** Human label for the current range (for eyebrows/subtitles). */
export function rangeLabel(range: TimeRange): string {
  if (range.key === 'last7') return 'last 7 days';
  if (range.key === 'last30') return 'last 30 days';
  if (range.from && range.to) return `${formatDay(range.from)} – ${formatDay(range.to)}`;
  return 'custom range';
}

/** Compact label for the Custom button itself, which has far less room. */
function customButtonLabel(range: TimeRange): string {
  const from = fromIsoDay(range.from);
  const to = fromIsoDay(range.to);
  if (range.key !== 'custom' || !from || !to) return 'Custom';
  const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
  const left = `${from.getUTCDate()} ${MONTHS[from.getUTCMonth()]}${
    sameYear ? '' : ` ${from.getUTCFullYear()}`
  }`;
  return `${left} – ${to.getUTCDate()} ${MONTHS[to.getUTCMonth()]} ${to.getUTCFullYear()}`;
}

/**
 * Range picker: Last 7 days / Last 30 days / Custom.
 *
 * Custom opens a two-month calendar. The draft selection is held locally and
 * lifted only on Apply, so dragging out a range does not fire a request for
 * every intermediate state — one change here refetches the whole page.
 */
export default function RangeControl({
  value,
  onChange
}: {
  value: TimeRange;
  onChange: (r: TimeRange) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const [monthCount, setMonthCount] = useState(2);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const customButtonRef = useRef<HTMLButtonElement>(null);

  const today = useMemo(() => utcToday(), []);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    customButtonRef.current?.focus();
  }, []);

  // Dismiss on outside click or Escape — expected of anything popover-shaped.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePopover();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closePopover]);

  // One month on narrow viewports; two side by side when there is room.
  useEffect(() => {
    const applyWidth = () => setMonthCount(window.innerWidth < TWO_MONTH_MIN_WIDTH ? 1 : 2);
    applyWidth();
    window.addEventListener('resize', applyWidth);
    return () => window.removeEventListener('resize', applyWidth);
  }, []);

  useEffect(() => {
    if (isOpen) popoverRef.current?.focus();
  }, [isOpen]);

  const selectPreset = (key: RangeKey) => {
    if (key === 'custom') {
      setDraft({
        from: fromIsoDay(value.from) || startOfRollingWindow(LAST_30_DAYS),
        to: fromIsoDay(value.to) || today
      });
      setIsOpen((open) => (value.key === 'custom' ? !open : true));
      return;
    }
    setIsOpen(false);
    onChange({ key });
  };

  const applyDraft = () => {
    if (!draft?.from) return;
    // A single click leaves `to` undefined until the second one; treat that as
    // a one-day range rather than refusing to apply.
    onChange({ key: 'custom', from: toIsoDay(draft.from), to: toIsoDay(draft.to || draft.from) });
    closePopover();
  };

  const draftLabel = draft?.from
    ? `${formatDay(toIsoDay(draft.from))} → ${formatDay(toIsoDay(draft.to || draft.from))}`
    : 'Pick a start date';

  return (
    <div className="range-control" ref={containerRef}>
      <div className="seg">
        {PRESETS.map((preset) => {
          const isActive = value.key === preset.key;
          const isCustom = preset.key === 'custom';
          return (
            <button
              key={preset.key}
              ref={isCustom ? customButtonRef : undefined}
              type="button"
              className={isActive ? 'on' : ''}
              aria-pressed={isActive}
              aria-haspopup={isCustom ? 'dialog' : undefined}
              aria-expanded={isCustom ? isOpen : undefined}
              onClick={() => selectPreset(preset.key)}
            >
              {isCustom ? customButtonLabel(value) : preset.label}
              {isCustom && <span className="range-caret" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      {isOpen && (
        <div
          className="range-popover"
          role="dialog"
          aria-label="Choose a custom date range"
          ref={popoverRef}
          tabIndex={-1}
        >
          <DayPicker
            mode="range"
            numberOfMonths={monthCount}
            selected={draft}
            onSelect={setDraft}
            defaultMonth={draft?.from}
            disabled={{ after: today }}
            timeZone="UTC"
            showOutsideDays={false}
            weekStartsOn={0}
          />
          <div className="range-popover-foot">
            <span className="range-draft">{draftLabel}</span>
            <div className="range-actions">
              <button type="button" className="btn" onClick={closePopover}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyDraft}
                disabled={!draft?.from}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
